import * as React from 'react';
import DatePicker from 'react-datepicker';
import './time-input.scss';
import { useRef } from 'react';
import * as IoC from 'typescript-ioc';
import { DateTimeFormatter } from '../../../infrastructure/implementations/date-formatter';
import { utcToZonedTime, zonedTimeToUtc } from '../../../infrastructure/utils/date-fns-overrides.utils';
import { TimeFormats } from '../../../infrastructure/date-formatter';

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);
const timeFormatRegex = /^(?:am|pm|)$/i;

const parseTimeString = (value: string): [string, string] => {
    let [hours, minutes] = value.split(':');
    if (!minutes) {
        switch (value.length) {
            case 4:
                hours = value.slice(0, 2);
                minutes = value.slice(2, 4);
                break;
            case 3:
                hours = value.slice(0, 1);
                minutes = value.slice(1, 3);
                break;
            default:
                hours = value;
        }
    }
    if (!hours) {
        minutes = value;
    }
    return [hours, minutes];
};

const validateHoursOrMinutesString = (value: string | undefined) => {
    return value && (Number.isNaN(+value) || value.length > 3);
};

const toHHMM = (value: string, timeFormat: TimeFormats.Day12Hours | TimeFormats.Day24Hours): string | null => {
    const trimmedValue = value.trim();
    const isTimeFormatSpecified = timeFormatRegex.exec(trimmedValue.slice(-2));

    if (
        !trimmedValue.length ||
        trimmedValue.length > 8 ||
        (!isTimeFormatSpecified && trimmedValue.length > 5)
    ) {
        return null;
    }

    let format = isTimeFormatSpecified && trimmedValue.slice(-2).toUpperCase();
    const time = isTimeFormatSpecified ? trimmedValue.slice(0, -2).trim() : trimmedValue;
    const [hours, minutes] = parseTimeString(time);
    let hoursNumber = hours ? Number.parseInt(hours, 10) : 0;
    const minutesNumber = minutes ? Number.parseInt(minutes, 10) : 0;

    if (
        validateHoursOrMinutesString(hours) ||
        validateHoursOrMinutesString(minutes) ||
        hoursNumber > 24 ||
        minutesNumber > 59 ||
        (hoursNumber > 11 && format)
    ) {
        return null;
    }

    if (timeFormat === TimeFormats.Day24Hours) {
        hoursNumber = format === 'PM' ? hoursNumber + 12 : hoursNumber;
        hoursNumber = hoursNumber === 24 ? 0 : hoursNumber;
    } else {
        format = format || (hoursNumber > 11 && hoursNumber !== 24 ? 'PM' : 'AM');
        hoursNumber = hoursNumber > 11 ? hoursNumber - 12 : hoursNumber;
        hoursNumber = hoursNumber === 12 ? 0 : hoursNumber;
    }

    const validatedMinutes = minutesNumber < 10 ? '0' + minutesNumber : minutesNumber.toString();
    const validatedHours = hoursNumber < 10 ? '0' + hoursNumber : hoursNumber.toString();
    return timeFormat === TimeFormats.Day24Hours ? `${validatedHours}:${validatedMinutes}` : `${validatedHours}:${validatedMinutes} ${format}`;
};

const dateToHHMM = (value: Date, timeFormat: TimeFormats.Day12Hours | TimeFormats.Day24Hours): string => {
    const hoursValue = value.getHours();
    const format = timeFormat === TimeFormats.Day12Hours && (hoursValue > 11 ? 'PM' : 'AM');
    const hours = timeFormat === TimeFormats.Day24Hours ? hoursValue : (hoursValue > 11 ? hoursValue - 12 : hoursValue);
    const minutes = value.getMinutes();
    const validatedMinutes = minutes < 10 ? '0' + minutes : minutes.toString();
    const validatedHours = hours < 10 ? '' + hours : hours.toString();
    return format ? `${validatedHours}:${validatedMinutes} ${format}` : `${validatedHours}:${validatedMinutes}`;
};

interface ITimeInputProps {
    className: string;
    date: Date;
    timeIntervals?: number;
    onChange: (date: Date) => void;
    onError?: () => void;
    timeZone?: string;
    disabled?: boolean;
}

const TimeInput = (props: ITimeInputProps) => {

    const timeFormat = dateTimeFormatter.getLocalTimeFormat();
    let finalDate = null;
    const [value, setValue] = React.useState('');

    React.useEffect(() => {
        finalDate = props.timeZone ? props.date && utcToZonedTime(props.date, props.timeZone) : props.date;
        const initialValue = finalDate ? dateToHHMM(finalDate, timeFormat) : '';
        setValue(initialValue);
    }, [props.date, props.timeZone]);

    const datePickerRef = useRef(null);
    const timeIntervals = props.timeIntervals || 15;
    const handleChange = (event): void => {
        setValue(event.target.value);
    };

    const handleBlur = (event): void => {
        const inputValue = toHHMM(event.target.value, timeFormat);
        if (inputValue) {
            setValue(inputValue);
            const [hours, minutes] = inputValue.split(':');
            let hoursNumber = Number.parseInt(hours, 10);
            const minutesNumber = Number.parseInt(minutes, 10);
            const format = minutes.split(' ')[1];
            hoursNumber = (timeFormat === TimeFormats.Day12Hours && format === 'PM') ? hoursNumber + 12 : hoursNumber;
            const date = finalDate ? new Date(finalDate.getTime()) : new Date();
            date.setHours(hoursNumber, minutesNumber, 0, 0);
            sendDate(date);
        } else if (!inputValue && !datePickerRef.current.isCalendarOpen()) {
            props.onError?.();
        }
    };

    const handleFocus = (): void => {
        datePickerRef.current.setOpen(true);
    };

    const handleDatePickerChange = (date: Date): void => {
        setValue(dateTimeFormatter.dateToFormattedString(date, 'H:mm'));
        sendDate(date);
    };

    const sendDate = (zonedDate: Date): void => {
        const utcDate = zonedTimeToUtc(zonedDate, props.timeZone);
        props.onChange(utcDate);
    };

    return (
        <div className='oe-time-input-wrapper'>
            <input
                className={`oe-time-input ${props.className}`}
                type='text'
                onChange={handleChange}
                onBlur={handleBlur}
                value={value}
                onFocus={handleFocus}
                disabled={props.disabled}
            />
            <DatePicker
                className={props.className}
                selected={finalDate}
                ref={datePickerRef}
                timeformat={dateTimeFormatter.getLocalTimeFormat()}
                showTimeSelectOnly={true}
                showTimeSelect={true}
                timeIntervals={timeIntervals}
                timeFormat={timeFormat}
                onChange={handleDatePickerChange}
                popperPlacement='bottom-start'
                popperModifiers={[
                  {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['bottom']
                    }
                  }
                ]}
            />
        </div>
    );
};

TimeInput.defaultProps = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
};

export { TimeInput, toHHMM, dateToHHMM };
